<template>
  <div class="vat-calculator">
    <div class="inputs">
      <div class="input-group">
        <label
          class="input-group__label"
          for="price"
          >Цена</label
        >
        <TextInputCustom
          id="price"
          type="text"
          :state="!isValidInput ? 'error' : ''"
          input-mode="decimal"
          :placeholder="'Введите цену'"
          :value="price"
          :model-value="price"
          @keydown="onKeydown"
          @update:model-value="validatePrice"
        />
      </div>

      <div class="input-group">
        <label class="input-group__label">Действие</label>
        <InputRadioGroup
          v-model="action"
          name="action"
          :options="[
            { id: 'deduct', title: 'Выделить НДС' },
            { id: 'add', title: 'Начислить НДС' },
          ]"
        />
      </div>

      <div class="input-group">
        <label class="input-group__label">Ставка НДС</label>
        <InputRadioGroup
          v-model="vatRate"
          name="vatRate"
          :options="[
            { id: '10', title: '10%' },
            { id: '20', title: '20%' },
          ]"
        />
      </div>

      <button
        class="calculate-button"
        :disabled="!price || !action || !vatRate"
        @click="calculateVAT"
      >
        {{ buttonTitle }}
      </button>
    </div>

    <div class="results">
      <div
        v-for="group in resultGroups"
        :key="group.title"
        class="result-group"
      >
        <span class="result-group__label">{{ group.title }}</span>
        <span class="result-group__value">{{ group.valueNumber }}</span>
        <span class="result-group__value--text">{{ group.valueText || emptyPriceString }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { rubles } from '~/libs/rubles';
  import InputRadioGroup from '@/components/InputRadio/InputRadioGroup.vue';
  import TextInputCustom from '@/components/TextInput/TextInput.vue';

  export default defineNuxtComponent({
    name: 'VatCalculator',

    components: {
      InputRadioGroup,
      TextInputCustom,
    },

    data() {
      return {
        emptyPriceString: 'ноль рублей 00 копеек',
        price: '',
        action: '',
        vatRate: '',
        isValidInput: true,
        buttonTitle: 'Рассчитать',
        vatAmount: 0,
        priceWithoutVAT: 0,
        priceWithVAT: 0,
        inputSeparator: ',',
        fractionDigits: 2,
        maxInputLength: 12,
        excludeSymbolsRegex: /[^0-9.,]/g,
        tmpValue: '',
      };
    },

    computed: {
      resultGroups(): Array<any> {
        return [
          {
            title: 'НДС',
            valueNumber: this.toDisplayString(this.vatAmount),
            valueText: rubles(this.vatAmount),
          },
          {
            title: 'Сумма без НДС',
            valueNumber: this.toDisplayString(this.priceWithoutVAT),
            valueText: rubles(this.priceWithoutVAT),
          },
          {
            title: 'Сумма с НДС',
            valueNumber: this.toDisplayString(this.priceWithVAT),
            valueText: rubles(this.priceWithVAT),
          },
        ];
      },
    },

    watch: {
      isValidInput(newValue) {
        !newValue &&
          setTimeout(() => {
            this.isValidInput = true;
          }, 1000);
      },
    },

    methods: {
      validatePrice(value: string, event: InputEvent) {
        this.isValidInput = true;

        const isPasteEvent = event.inputType === 'insertFromPaste';
        const target = event.target as HTMLInputElement;

        const targetSelectionEnd = target.selectionEnd ?? 0;
        const tmpValueSpaces = this.countSpacesUntil(this.tmpValue, targetSelectionEnd, false);
        let updatedSelectionStart = targetSelectionEnd - tmpValueSpaces;

        // Разрешаем пробелы в вводе, при выделении не сбрасываются
        if (!isPasteEvent && /[^0-9., ]/.test(value)) {
          this.isValidInput = false;
        }

        let newValue = value
          .replace(this.excludeSymbolsRegex, '')
          .replace(/\s+/g, '')
          .replace(/\./g, this.inputSeparator);

        // Числа не могут начинаться с нуля, потому заменяем на введеное число
        if (newValue.length >= 2 && newValue.startsWith('0')) {
          newValue = isPasteEvent ? newValue.replace(/^0+/g, '') : newValue.slice(1);
        }

        // Число не может начинаться с разделителя потому заменяем на нуль с запятой
        if (newValue.startsWith(',')) {
          newValue = newValue.replace(',', '0,');
          updatedSelectionStart += 1;
        }

        if (isPasteEvent && (!newValue.length || newValue === this.price)) {
          this.isValidInput = false;
        }

        // Обрабатываем удаление пробела, вместе с ним должна удалиться цифра перед ним
        if (
          event.inputType === 'deleteContentBackward' &&
          this.tmpValue[targetSelectionEnd] === ' '
        ) {
          newValue =
            newValue.slice(0, updatedSelectionStart - 1) + newValue.slice(updatedSelectionStart);
          updatedSelectionStart -= 1;
        }

        // eslint-disable-next-line prefer-const
        let [integerPart, decimals, ...rest] = newValue.split(this.inputSeparator);
        const [previousIntegerPart, previousDecimals = ''] = this.price.split(this.inputSeparator);

        if (integerPart?.length > this.maxInputLength) {
          if (isPasteEvent && previousIntegerPart?.length < this.maxInputLength) {
            integerPart = integerPart.slice(0, this.maxInputLength);
          } else {
            this.$logger(`VatCalculator, price input too long, max allowed ${this.maxInputLength}`);
            this.isValidInput = false;
          }
        }

        if (decimals?.length > this.fractionDigits) {
          if (isPasteEvent && previousDecimals?.length < this.fractionDigits) {
            // Обрезаем до разрешнного если скопировали с большим колличеством знаков
            decimals = decimals.slice(0, this.fractionDigits);
          } else {
            this.$logger(
              `VatCalculator, too many digits after separator, max allowed ${this.fractionDigits}`,
            );
            // Разрешаем ограниченое количество знаков после разделителя при вводе
            this.isValidInput = false;
          }
        }

        if (rest.length && !isPasteEvent) {
          this.$logger('VatCalculator, input after fractions not allowed');
          // Не может быть несколько разделителей, после запятой идут копейки, дальше некуда
          this.isValidInput = false;
        }

        // Если пользователь поставил разделитель то включаем пустое значение decimals
        if (newValue.includes(this.inputSeparator)) {
          newValue = [integerPart, decimals].join(this.inputSeparator);
        } else {
          // Если запятой нет то не вставляем ее
          newValue = integerPart;
        }

        if (newValue.length > 3) {
          // Пробел каждый 3 символа
          newValue = newValue.replace(/(\d)(?=(\d{3})+(,|$))/g, '$1 ');
        }

        // Обновляем только если нет ошибок
        // Обновление target требуется всегда, иначе происходит рассинхрон
        target.value = this.isValidInput ? newValue : this.price;
        this.price = this.isValidInput ? newValue : this.price;

        this.$nextTick(() => {
          if (!this.isValidInput) {
            // Учитываем что курсор не должен меняться на длинну которую пытаются ввести
            // При вставке из буфера будет больше 1
            const characterChange = event?.data?.length ?? 0;
            target.setSelectionRange(
              targetSelectionEnd - characterChange,
              targetSelectionEnd - characterChange,
            );
            return;
          }
          // Считаем добавленные пробелы чтобы учесть их в подсчете позиции курсора
          // Считается только до курсора, чтобы не учитывать лишнее при редактировании в середине
          // При селекте selectionEnd не включается в подсчет
          const spaces = this.countSpacesUntil(newValue, targetSelectionEnd);
          // Восстанавливаем позицию курсора, т.к. сбрасывается при обновлении
          target.setSelectionRange(updatedSelectionStart + spaces, updatedSelectionStart + spaces);
        });
      },

      onKeydown(event: any): void {
        const { selectionStart, selectionEnd, value } = event.target;

        if (selectionStart !== selectionEnd) {
          return;
        }

        if (event.key === 'ArrowLeft' && value[selectionStart - 1] === ' ') {
          event.target.setSelectionRange(selectionStart - 2, selectionEnd, 'backward');
        }

        if (event.key === 'ArrowRight' && value[selectionStart + 1] === ' ') {
          event.target.setSelectionRange(selectionStart + 1, selectionEnd + 1);
        }

        if (
          !this.excludeSymbolsRegex.test(event.key) ||
          (event.key === 'Backspace' && event.target.selectionStart > 0)
        ) {
          // Временно сохраняем значение чтобы можно было найти удаленный кусок в обработке инпута
          this.tmpValue = value;
        }
      },

      toDisplayString(value: number): string {
        return value.toLocaleString('ru-RU', {
          maximumFractionDigits: value > 0 ? this.fractionDigits : 0,
          minimumFractionDigits: value > 0 ? this.fractionDigits : 0,
          style: 'currency',
          currency: 'RUB',
        });
      },

      countSpacesUntil(value: string, limit: number, checkTrailingSpaces: boolean = true): number {
        let count = 0;
        for (let i = 0; i < limit + (checkTrailingSpaces ? 1 : 0); i++) {
          value[i] === ' ' && count++;
        }
        return count;
      },

      calculateVAT() {
        const price = parseFloat(this.price.replace(/\s+/g, '').replace(',', '.'));
        const vatRate = parseInt(this.vatRate);

        if (this.action === 'add') {
          const vatAmountNumber = (price * vatRate) / 100;

          this.vatAmount = vatAmountNumber;
          this.priceWithVAT = price + vatAmountNumber;
          this.priceWithoutVAT = price;
        } else if (this.action === 'deduct') {
          const vatAmountNumber = (price * vatRate) / (100 + vatRate);

          this.vatAmount = vatAmountNumber;
          this.priceWithoutVAT = price - vatAmountNumber;
          this.priceWithVAT = price;
        }

        this.buttonTitle = 'Рассчитать заново';
      },
    },
  });
</script>

<style lang="scss" scoped>
  .vat-calculator {
    background-color: #f3f5fc;
    display: flex;
    flex-direction: column;
    margin: 0px -16px 12px;
    padding-bottom: 32px;

    @include tablet {
      flex-direction: row;
      gap: 16px;
      margin: 0;
      margin-bottom: 12px;
      padding: 32px 24px;
    }

    @include big-tablet {
      margin: 0 -24px 12px;
    }

    .inputs {
      display: flex;
      flex: 50%;
      flex-direction: column;
      gap: 16px;
      padding: 32px 16px 24px;

      @include tablet {
        padding: 0;
      }

      .input-group {
        display: flex;
        flex-direction: column;
        gap: 8px;

        &__label {
          @include normalText;
          font-family: $secondFont;
          font-weight: 600;
        }
      }

      :deep() {
        .input-radio-group {
          display: flex;
          flex-direction: column;
          gap: 8px;

          &__item {
            align-items: center;
            background-color: white;
            border: 1px solid $borderColor;
            border-radius: 4px;
            display: flex;
            font-family: $mainFont;
            gap: 16px;
            margin: 0;
            padding: 12px 16px;

            .radio__box {
              left: 0;
              position: relative;
              top: 0;
            }
          }
        }
      }

      .calculate-button {
        @include normalText;

        font-family: $mainFont;
        background-color: $secondary;
        border: none;
        border-radius: 2px;
        color: white;
        cursor: pointer;
        font-weight: 700;
        padding: 12px 24px;
        width: 100%;

        &:disabled {
          background-color: #d4d7de;
          cursor: default;
        }

        @include hover {
          &:not(:disabled) {
            background-color: #264eb1;
          }
        }
      }
    }

    .results {
      background-color: white;
      border-top: 2px solid $secondary;
      display: flex;
      flex: 50%;
      flex-direction: column;
      gap: 16px;
      padding: 24px 16px;

      @include tablet {
        padding: 24px;
      }

      .result-group {
        display: flex;
        flex-direction: column;
        gap: 4px;

        &:not(:last-child) {
          border-bottom: 1px solid $borderColor;
        }

        > *:last-child {
          margin-bottom: 16px;
        }

        &__label {
          @include normalText;
          font-family: $mainFont;
        }

        &__value {
          @include normalText;
          font-family: $secondFont;
          font-weight: 600;

          &--text {
            @include smallText;
            color: $caption-color;
            font-family: $mainFont;
          }
        }
      }
    }
  }
</style>
